import type {PageObject} from '@PosterWhiteboard/page/page.types';
import type {PageItemsObject} from '@PosterWhiteboard/page/page-items.class';
import type {DeepPartial} from '@/global';
import type {AudioClipsObject} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {getItemPageEndTime, hasItemTimingFields} from '@PosterWhiteboard/items/item/item-timing.library';
import {isTranscriptItemObject} from '@PosterWhiteboard/items/item/item.library';
import {getPlaybackDuration} from '@Libraries/audio-player/audio-player.library';
import {PAGE_END_TIME_TOLERANCE_SECONDS} from '@PosterWhiteboard/poster/poster.types';

/**
 * Pure page-duration math, intentionally free of any Fabric/canvas imports so the timeline UI, the
 * Redux selectors, and unit tests can depend on it without loading the editor's heavy item pipeline.
 * The live `Page.getDuration()` reuses the same `combinePageDuration` formula, so the playback clock's
 * max and the timeline's max are always derived identically.
 */

/** Minimum playback duration, in seconds, given to a static (non-video) page in webpage playback. */
export const WEBPAGE_STATIC_PAGE_DURATION = 5;

/**
 * Combines a page's component spans into its effective duration. Audio and subtitles can each raise
 * the result above the visual span so playback runs long enough to play the audio out and reach
 * transcript items beyond the background. This is the single duration formula: both the Redux-side
 * `computePageDuration` and the live `Page.getDuration()` call it with the same three terms, so the
 * timeline's max and the playback clock's max can never drift apart.
 */
export const combinePageDuration = (backgroundVisualDuration: number, maxAudioEnd: number, maxSubtitleEnd: number): number => {
  return Math.max(backgroundVisualDuration, maxAudioEnd, maxSubtitleEnd);
};

/**
 * Computes the effective duration of a page from its stable child references.
 * The accumulative background duration is the base, raised by any non-background
 * item whose bar extends past the last background — items own their spans, so the
 * longest item governs when it lies beyond the background sequence. Audio clips
 * and subtitles can each raise the result further so playback runs long enough to
 * play the audio out and reach transcript items beyond the visual span. Empty
 * pages return 0.
 *
 * This is the single source of truth for page duration: `getPageDuration` and the
 * `getCurrentPageDuration` selector both delegate here, and it mirrors
 * `Page.getDuration()` (both combine their terms via `combinePageDuration`, and
 * `getItemPageEndTime` mirrors the live items' `getPageEndTime()` overrides).
 */
export const computePageDuration = (items: Pick<PageItemsObject, 'itemsHashMap'>, audioClips: AudioClipsObject | undefined): number => {
  const allItems = Object.values(items.itemsHashMap);
  let bgSum = 0;
  let maxItemEndTime = 0;
  for (const item of allItems) {
    if (item.isBackground) {
      bgSum += getItemPageEndTime(item) - item.onPageStartTime;
    } else {
      maxItemEndTime = Math.max(maxItemEndTime, getItemPageEndTime(item));
    }
  }
  const baseDuration = Math.max(bgSum, maxItemEndTime);
  let maxAudioEnd = 0;
  if (audioClips) {
    for (const audio of Object.values(audioClips.audioItemsHashMap)) {
      const end = audio.onPageStartTime + getPlaybackDuration(audio.audioPlayer);
      if (end > maxAudioEnd) {
        maxAudioEnd = end;
      }
    }
  }
  let maxSubtitleEnd = 0;
  for (const item of allItems) {
    if (isTranscriptItemObject(item)) {
      for (const subtitle of Object.values(item.subtitlesHashmap)) {
        maxSubtitleEnd = Math.max(maxSubtitleEnd, subtitle.endTime);
      }
    }
  }
  return combinePageDuration(baseDuration, maxAudioEnd, maxSubtitleEnd);
};

/**
 * Whether a partial items object carries every field `computePageDuration` reads: each item's timing
 * for its type and, for transcripts, the subtitles. A load or history snapshot passes; a field patch
 * does not. Narrows only the hashmap — order and groups are not checked.
 */
export const hasCompleteItemTiming = (items: DeepPartial<PageItemsObject>): items is Pick<PageItemsObject, 'itemsHashMap'> => {
  if (!items.itemsHashMap) {
    return false;
  }
  return Object.values(items.itemsHashMap).every((item) => item !== undefined && hasItemTimingFields(item) && hasSubtitlesIfTranscript(item));
};

const hasSubtitlesIfTranscript = (item: ItemObject): boolean => {
  return !isTranscriptItemObject(item) || item.subtitlesHashmap !== undefined;
};

export const getPageDuration = (page: PageObject): number => {
  return computePageDuration(page.items, page.audioClips);
};

export interface PageGrowthStretchTargets {
  /** The background that should absorb the page growth; null when there is none or the last background is a video (plays once, span trim-derived). */
  lastBackgroundUid: string | null;
  /** Non-background items whose span ended at the previous visual duration — the "full-span" items that should follow the page to its new end. */
  fullSpanItemUids: string[];
}

/**
 * Decides what must stretch when the page's visual duration grows (an added or grown item
 * outran the page): the last background plus every item that previously ended at the page end.
 * Items the user trimmed short of the page end are left alone. Ends are matched within
 * PAGE_END_TIME_TOLERANCE_SECONDS, mirroring the playback-end tolerance. `growingItemUid`
 * names the item driving the growth (e.g. a slideshow whose slides grew) — already on the
 * page, so it must not be picked up as a full-span item and re-stretched.
 */
export const getPageGrowthStretchTargets = (items: PageItemsObject, previousVisualDuration: number, growingItemUid?: string): PageGrowthStretchTargets => {
  let lastBackground: ItemObject | null = null;
  const fullSpanItemUids: string[] = [];
  for (const item of Object.values(items.itemsHashMap)) {
    if (item.isBackground) {
      if (lastBackground === null || item.onPageStartTime > lastBackground.onPageStartTime) {
        lastBackground = item;
      }
    } else if (item.uid !== growingItemUid && Math.abs(getItemPageEndTime(item) - previousVisualDuration) <= PAGE_END_TIME_TOLERANCE_SECONDS) {
      fullSpanItemUids.push(item.uid);
    }
  }
  const lastBackgroundUid = lastBackground !== null && lastBackground.gitype !== ITEM_TYPE.VIDEO ? lastBackground.uid : null;
  return {lastBackgroundUid, fullSpanItemUids};
};
